// v1.22.0 · Auditoría · Panel de lo macro a lo micro:
//   Nivel 1  panorama: puntaje, ecuación contable, indicadores, mapa de riesgo
//   Nivel 2  estructura: mapa de árbol del balance o del resultado con desglose por niveles (clic = bajar)
//   Nivel 3  cuenta: saldos y movimientos mes a mes, mayores débitos/créditos, banderas y aclaraciones
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, ChevronRight, Layers } from 'lucide-react';
import { C, SEV, api, errMsg, fdate, money, pct, plab, short } from './api';
import { Gauge, Heatmap, Lines, MonthBars, Spark, Treemap, Waterfall } from './charts';

function Kpi({ label, value, sub, tone, spark }) {
  return (
    <div className="neu-card" style={{ padding: 16 }}>
      <div className="neu-label">{label}</div>
      <div className="neu-kpi tabular" style={{ color: tone || C.ink, marginTop: 6 }}>{value}</div>
      <div className="neu-row" style={{ marginTop: 4, justifyContent: 'space-between' }}>
        <span className="neu-sub">{sub}</span>{spark}
      </div>
    </div>
  );
}

export function SevTag({ s }) {
  const v = SEV[s] || SEV.baja;
  return <span className="neu-sev" style={{ color: v.color }}>{v.icon} {v.label}</span>;
}

export default function Panel({ cid, analysis, onOpenFlag, goTab }) {
  const [scope, setScope] = useState('balance');   // balance | resultados
  const [path, setPath] = useState([]);            // nodos del desglose
  const [account, setAccount] = useState(null);
  const a = analysis;
  const k = a?.kpis || {};
  const months = a?.series?.months || [];

  useEffect(() => { setPath([]); setAccount(null); }, [scope, cid]);

  const roots = useMemo(() => (a?.tree || []).filter((n) => (scope === 'balance' ? '123' : '456').includes(n.code[0])), [a, scope]);
  const level = path.length ? path[path.length - 1].children : roots;

  const pick = async (n) => {
    if (n.children?.length) { setPath([...path, n]); return; }
    try { setAccount(await api.get(`/companies/${cid}/accounts/${encodeURIComponent(n.code)}/detail`)); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    const last = path[path.length - 1];
    if (!last) { setAccount(null); return; }
    api.get(`/companies/${cid}/accounts/${encodeURIComponent(last.code)}/detail`).then(setAccount).catch(() => setAccount(null));
  }, [path, cid]);

  if (!a) return null;
  if (!a.sources?.balance && !a.sources?.ledger_periods) {
    return (
      <div className="neu-card neu-empty" style={{ padding: 40 }}>
        <Layers size={28} style={{ color: C.brand }} />
        <p style={{ fontWeight: 700, color: C.ink, marginTop: 10 }}>Todavía no hay estados financieros ni mayores para esta empresa.</p>
        <p className="neu-hint">Suba el plan de cuentas, el balance, el estado de resultados y los mayores (o el .zip completo del cierre) en «Expediente».</p>
        <button type="button" className="neu-btn neu-btn-primary" style={{ marginTop: 12 }} onClick={() => goTab('expediente')}>Ir al expediente</button>
      </div>
    );
  }
  const eqOk = Math.abs(k.ecuacion_diferencia || 0) <= 1;
  const sc = a.score || {};
  const ingM = months.map((m) => m.ingresos_mes);
  const gasM = months.map((m) => m.gastos_mes);

  return (
    <div className="neu-grid" style={{ gap: 22 }}>
      {/* ───── Nivel 1: panorama ───── */}
      <section className="neu-grid neu-grid-2" aria-label="Panorama">
        <div className="neu-card" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <Gauge score={sc.score} label={sc.label} />
            <div className="neu-sub">Puntaje de control interno</div>
          </div>
          <div className="neu-grid" style={{ gap: 10 }}>
            {['alta', 'media', 'baja'].map((s) => (
              <button type="button" key={s} className="neu-btn" style={{ justifyContent: 'space-between' }} onClick={() => goTab('banderas', { severity: s })}>
                <SevTag s={s} /><span className="tabular" style={{ fontSize: 18, fontWeight: 800 }}>{sc.open?.[s] ?? 0}</span>
              </button>
            ))}
            <div className="neu-hint">{sc.clarified || 0} aclarada(s) · {sc.closed || 0} cerrada(s). Corte {fdate(a.corte)} · materialidad {money(a.materialidad, 0)}.</div>
          </div>
        </div>
        <div className="neu-card">
          <div className="neu-row"><p className="neu-title">Ecuación contable</p><span className="neu-spacer" />
            <span className={`neu-chip ${eqOk ? 'neu-chip-green' : 'neu-chip-red'}`}>{eqOk ? '✓ Cuadra' : `Descuadre ${money(k.ecuacion_diferencia)}`}</span></div>
          <EquationBar k={k} />
          <div className="neu-grid neu-grid-3" style={{ gap: 10, marginTop: 12 }}>
            <Ratio label="Liquidez corriente" value={k.liquidez ?? '—'} warn={k.liquidez !== null && k.liquidez < 1} hint="Activo corriente / pasivo corriente" />
            <Ratio label="Endeudamiento" value={pct(k.endeudamiento)} warn={k.endeudamiento > 0.7} hint="Pasivo / activo" />
            <Ratio label="Margen neto" value={pct(k.margen)} warn={k.margen < 0} hint="Resultado / ingresos" />
          </div>
          {a.pymes && (
            <div className="neu-inset" style={{ marginTop: 12, fontSize: 12.5, lineHeight: 1.5 }}>
              <b>Marco: NIIF para las PYMES (Ecuador)</b>{' '}
              <span className={`neu-chip ${a.pymes.califica ? 'neu-chip-green' : 'neu-chip-amber'}`}>{a.pymes.califica ? '✓ Califica como PYME' : '▲ Revisar calificación'}</span>
              <div className="neu-hint" style={{ marginTop: 4 }}>
                Activos {short(a.pymes.activos)} (límite {short(a.pymes.limites?.activos)}) · ventas brutas anualizadas {short(a.pymes.ventas_anualizadas)}
                {' '}(límite {short(a.pymes.limites?.ventas)}, base {a.pymes.meses_base} meses){a.pymes.trabajadores != null ? ` · ${a.pymes.trabajadores} trabajadores (límite ${a.pymes.limites?.trabajadores})` : ''}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="neu-grid neu-grid-kpi" aria-label="Indicadores">
        <Kpi label="Activo" value={short(k.activo)} sub={`Efectivo ${short(k.efectivo)}`} />
        <Kpi label="Pasivo" value={short(k.pasivo)} sub={`Corriente ${short(k.pasivo_corriente)}`} />
        <Kpi label="Patrimonio" value={short(k.patrimonio)} sub={`Capital de trabajo ${short(k.capital_trabajo)}`} />
        <Kpi label="Ingresos" value={short(k.ingresos)} sub="Acumulado" spark={<Spark values={ingM} color={C.s1} />} />
        <Kpi label="Gastos" value={short(k.gastos)} sub="Acumulado" spark={<Spark values={gasM} color={C.s2} />} />
        <Kpi label="Resultado" value={short(k.resultado)} tone={k.resultado < 0 ? SEV.alta.color : C.good}
             sub={k.resultado < 0 ? 'Pérdida del período' : 'Utilidad del período'} />
      </section>

      <section className="neu-grid neu-grid-2">
        <div className="neu-card">
          <p className="neu-title">Mapa de riesgo por área</p>
          <p className="neu-sub" style={{ marginBottom: 12 }}>Banderas abiertas por severidad. Clic en un área para ver sus banderas.</p>
          <Heatmap rows={a.heatmap} onPick={(area) => goTab('banderas', { area })} />
        </div>
        <div className="neu-card">
          <p className="neu-title">Cascada del resultado</p>
          <p className="neu-sub" style={{ marginBottom: 8 }}>De los ingresos al resultado del período.</p>
          <Waterfall steps={a.waterfall} resultado={k.resultado} />
        </div>
      </section>

      <section className="neu-grid neu-grid-2">
        <div className="neu-card">
          <p className="neu-title">Ingresos y gastos de cada mes</p>
          <p className="neu-sub" style={{ marginBottom: 8 }}>Desde el libro mayor acumulado, con el resultado acumulado.</p>
          <MonthBars months={months} series={[{ key: 'ingresos_mes', label: 'Ingresos', color: C.s1 }, { key: 'gastos_mes', label: 'Gastos', color: C.s2 }]}
                     line={{ key: 'resultado_acum', label: 'Resultado acumulado', color: C.ink }} />
        </div>
        <div className="neu-card">
          <p className="neu-title">Saldos al cierre de cada mes</p>
          <p className="neu-sub" style={{ marginBottom: 8 }}>Activo, pasivo y patrimonio reconstruidos desde el mayor.</p>
          <Lines months={months} series={[{ key: 'activos', label: 'Activo', color: C.s1 }, { key: 'pasivos', label: 'Pasivo', color: C.s2 }, { key: 'patrimonio', label: 'Patrimonio', color: C.s3 }]} />
        </div>
      </section>

      {a.budget?.months?.length > 0 && (
        <section className="neu-card">
          <p className="neu-title">Presupuesto vs. ejecución</p>
          <p className="neu-sub" style={{ marginBottom: 8 }}>{a.budget.source}</p>
          <div className="neu-grid neu-grid-2">
            <MonthBars months={a.budget.months} series={[{ key: 'ing_p', label: 'Ingresos presupuestados', color: '#86b6ef' }, { key: 'ing_e', label: 'Ingresos ejecutados', color: C.s1 }]} height={220} />
            <MonthBars months={a.budget.months} series={[{ key: 'gas_p', label: 'Gastos presupuestados', color: '#f3a27f' }, { key: 'gas_e', label: 'Gastos ejecutados', color: C.s2 }]} height={220} />
          </div>
          {a.budget.desvios?.length > 0 && (
            <div className="neu-scroll" style={{ marginTop: 12, maxHeight: 260 }}>
              <table className="neu-table"><thead><tr><th>Cuenta de gasto</th><th className="num">Presupuesto</th><th className="num">Ejecución</th><th className="num">Desvío</th></tr></thead>
                <tbody>{a.budget.desvios.map((d) => <tr key={d.code}><td>{d.code} {d.name}</td><td className="num">{money(d.presupuesto)}</td><td className="num">{money(d.ejecucion)}</td>
                  <td className="num" style={{ color: d.desvio > 0 ? SEV.alta.color : C.good, fontWeight: 700 }}>{d.desvio > 0 ? '+' : ''}{money(d.desvio)}</td></tr>)}</tbody></table>
            </div>
          )}
        </section>
      )}

      {/* ───── Nivel 2: estructura con desglose ───── */}
      <section className="neu-card" aria-label="Estructura de cuentas">
        <div className="neu-row" style={{ marginBottom: 10 }}>
          <div><p className="neu-title">Estructura de cuentas: de lo macro a lo micro</p>
            <p className="neu-sub">Tamaño = saldo. Borde de color = cuenta con banderas. Clic para bajar un nivel; en una cuenta final se abre su detalle.</p></div>
          <span className="neu-spacer" />
          <button type="button" className={`neu-btn neu-btn-sm ${scope === 'balance' ? 'is-on' : ''}`} onClick={() => setScope('balance')}>Balance</button>
          <button type="button" className={`neu-btn neu-btn-sm ${scope === 'resultados' ? 'is-on' : ''}`} onClick={() => setScope('resultados')}>Resultados</button>
        </div>
        <nav className="neu-crumbs" aria-label="Nivel" style={{ marginBottom: 10 }}>
          <button type="button" disabled={!path.length} onClick={() => setPath([])}>{scope === 'balance' ? 'Balance' : 'Resultados'}</button>
          {path.map((n, i) => (
            <React.Fragment key={n.code}><ChevronRight size={14} color={C.muted} />
              <button type="button" disabled={i === path.length - 1} onClick={() => setPath(path.slice(0, i + 1))}>{n.code} {n.name}</button></React.Fragment>
          ))}
        </nav>
        <Treemap nodes={level || []} onPick={pick} height={path.length ? 300 : 340} />
      </section>

      {/* ───── Nivel 3: la cuenta ───── */}
      {account && <AccountDetail acc={account} onOpenFlag={onOpenFlag} />}
    </div>
  );
}

function EquationBar({ k }) {
  const tot = Math.max(k.activo || 0, (k.pasivo || 0) + (k.patrimonio || 0), 1);
  const seg = (v, col, lab) => (v > 0 ? <div title={`${lab}: ${money(v)}`} style={{ width: `${(v / tot) * 100}%`, background: col, color: '#fff', fontSize: 11, fontWeight: 700,
    padding: '6px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', borderRight: '2px solid #e9edf3' }}>{lab} {short(v)}</div> : null);
  return (
    <div className="neu-grid" style={{ gap: 8, marginTop: 10 }}>
      <div className="neu-inset" style={{ padding: 4, display: 'flex', borderRadius: 12, overflow: 'hidden' }}>
        {seg(k.activo_corriente, C.s1, 'Activo corriente')}{seg((k.activo || 0) - (k.activo_corriente || 0), '#5598e7', 'No corriente')}
      </div>
      <div className="neu-inset" style={{ padding: 4, display: 'flex', borderRadius: 12, overflow: 'hidden' }}>
        {seg(k.pasivo_corriente, C.s2, 'Pasivo corriente')}{seg((k.pasivo || 0) - (k.pasivo_corriente || 0), '#d95926', 'No corriente')}
        {seg(k.patrimonio, C.s3, 'Patrimonio')}
      </div>
    </div>
  );
}

function Ratio({ label, value, warn, hint }) {
  return (
    <div className="neu-card-flat" title={hint}>
      <div className="neu-label">{label}</div>
      <div className="neu-row" style={{ marginTop: 4 }}>
        <span className="tabular" style={{ fontSize: 18, fontWeight: 800, color: warn ? SEV.alta.color : C.ink }}>{value}</span>
        {warn && <AlertTriangle size={14} color={SEV.alta.color} aria-label="Atención" />}
      </div>
    </div>
  );
}

function AccountDetail({ acc, onOpenFlag }) {
  const s = acc.series || [];
  const last = s[s.length - 1];
  return (
    <section className="neu-card" aria-label="Detalle de la cuenta">
      <div className="neu-row">
        <div><p className="neu-label">Cuenta {acc.code} · naturaleza {acc.nature === 'D' ? 'deudora' : 'acreedora'}</p>
          <p className="neu-h1" style={{ fontSize: 19 }}>{acc.name}</p></div>
        <span className="neu-spacer" />
        {last && <div style={{ textAlign: 'right' }}><div className="neu-label">Saldo {plab(last.period)}</div>
          <div className="neu-kpi tabular" style={{ color: last.saldo < 0 ? SEV.alta.color : C.ink }}>{money(last.saldo)}</div></div>}
      </div>
      {acc.account?.notes && <div className="neu-inset neu-hint" style={{ marginTop: 10 }}><b>Aclaración del plan:</b> {acc.account.notes}</div>}
      <div className="neu-grid neu-grid-2" style={{ marginTop: 14 }}>
        <div>
          <p className="neu-title">Movimiento y saldo de cada mes</p>
          <MonthBars months={s} series={[{ key: 'debe', label: 'Debe', color: C.s1 }, { key: 'haber', label: 'Haber', color: C.s2 }]}
                     line={{ key: 'saldo', label: 'Saldo al cierre', color: C.ink }} height={230} />
        </div>
        <div>
          <p className="neu-title">Banderas de esta cuenta</p>
          {acc.flags?.length ? acc.flags.map((f) => (
            <button type="button" key={f.key} className="neu-btn" style={{ width: '100%', justifyContent: 'flex-start', marginTop: 8, textAlign: 'left', whiteSpace: 'normal' }} onClick={() => onOpenFlag(f.key)}>
              <SevTag s={f.severity} /><span style={{ fontWeight: 600 }}>{f.title}</span>
            </button>
          )) : <p className="neu-hint">Sin banderas.</p>}
          {acc.children?.length > 0 && (
            <>
              <p className="neu-title" style={{ marginTop: 14 }}>Subcuentas</p>
              <table className="neu-table"><tbody>{acc.children.map((c) => <tr key={c.code}><td>{c.code} {c.name}</td><td className="num">{money(c.saldo)}</td></tr>)}</tbody></table>
            </>
          )}
        </div>
      </div>
      <div className="neu-grid neu-grid-2" style={{ marginTop: 14 }}>
        <TopMoves title="Mayores débitos" icon={<ArrowUpRight size={14} />} rows={acc.top_debe} field="debe" />
        <TopMoves title="Mayores créditos" icon={<ArrowDownRight size={14} />} rows={acc.top_haber} field="haber" />
      </div>
    </section>
  );
}

function TopMoves({ title, icon, rows = [], field }) {
  return (
    <div>
      <p className="neu-title" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>{icon}{title}</p>
      <div className="neu-scroll" style={{ maxHeight: 300 }}>
        <table className="neu-table">
          <thead><tr><th>Fecha</th><th>Asiento / detalle</th><th className="num">Valor</th></tr></thead>
          <tbody>{rows.filter((r) => r[field]).map((r, i) => (
            <tr key={i}><td style={{ whiteSpace: 'nowrap' }}>{fdate(r.date)}</td>
              <td><b style={{ fontSize: 11.5 }}>{r.asiento}</b> · {r.persona && r.persona !== '-' ? `${r.persona} · ` : ''}<span style={{ color: '#4b5563' }}>{(r.desc || '').slice(0, 90)}</span></td>
              <td className="num">{money(r[field])}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
